import React, { ReactNode, RefObject } from 'react';
import { createPortal } from 'react-dom';
import colors from '../../helpers/colors';
import Icon from '../Icon';

const useOpenInNewTabIcons = (
  ref: RefObject<HTMLDivElement>,
  html: string,
  defaultIcon?: string,
  externalOnlyIcon?: string
): ReactNode => {
  const [iconSpans, setIconSpans] = React.useState<HTMLSpanElement[]>([]);

  const insertIconSpanIntoElement = (element: HTMLElement): HTMLSpanElement => {
    const span = document.createElement('span');
    span.classList.add('open-in-new-tab');
    if (!defaultIcon && !externalOnlyIcon) {
      span.setAttribute('role', 'link');
      span.setAttribute('aria-label', '(Opens in a new window)');
    }
    element.insertAdjacentElement('beforeend', span);
    return span;
  };

  React.useEffect(() => {
    if (ref.current) {
      const selector = defaultIcon
        ? 'a[target="_blank"]'
        : 'a[target="_blank"][data-external]';
      const openInNewTabAnchorItems = Array.from(
        ref.current.querySelectorAll<HTMLAnchorElement>(selector)
      );

      const spans = openInNewTabAnchorItems.map((a) => {
        const existingSpan = a.querySelector<HTMLSpanElement>('span.open-in-new-tab');
        return existingSpan ?? insertIconSpanIntoElement(a);
      });

      setIconSpans(spans);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, html]);

  const mapIcon = (icon: string): React.ReactPortal[] =>
    iconSpans.map((span) =>
      createPortal(
        <Icon
          name={icon}
          size="small"
          color={colors.core01}
          aria-label="Opens in new tab"
        />,
        span
      )
    );

  const customIcons = defaultIcon
    ? mapIcon(defaultIcon)
    : externalOnlyIcon
    ? mapIcon(externalOnlyIcon)
    : null;
  return customIcons;
};

export default useOpenInNewTabIcons;
